import {Button, Select, SelectItem, Slider, Switch, useDisclosure} from "@heroui/react";
import _ from 'lodash';
import Models from './Models.jsx';
import Voices from './Voices.jsx';
import {useMemo} from 'react';

export default function TextToSpeechSettings({allVoices = [], form, config, models}) {
	const {isOpen, onOpen, onOpenChange} = useDisclosure();
	
	const selectedModel = useMemo(() => {
		return _.find(models, (item) => item?.model_id == form.values?.model)
	}, [form.values?.model])
	
	
	const currentFields = useMemo(() => {
		return _.keys(_.get(config?.settings_key, selectedModel?.provider))
	}, [selectedModel?.provider])
	
	const onResetValues = async () => {
		const defaultSpeaker = await _.head(allVoices)
		const firstModel = _.find(models, item => item?.provider == 'edge_tts')
		form.setFieldValue('model', '')
		form.setFieldValue('voice_model', '')
		
		setTimeout(() => {
			const defaultValues = {
				model: firstModel?.model_id || '',
				voice_model: firstModel?.provider || '',
				voice_id: defaultSpeaker?.voice_id || '',
				emotion: '',
				language: 'auto',
				speed: 1,
				stability: 0.5,
				style: 0.4,
				use_speaker_boost: false,
			}
			form.setValues(_.merge(form.values, defaultValues))
		}, 300)
		
		console.log('Reset config values:', form.values)
	}
	
	return (
		<>
			<div className="w-full h-full min-h-full">
				<div className="grid grid-cols-2 gap-y-4 items-center w-full">
					<div className="col-span-full">
						<h3 className="font-semibold text-medium text-slate-800 mb-2">
							Model
						</h3>
						<div className="w-full">
							<Models form={form} models={models}/>
						</div>
					</div>
					<div className="col-span-full">
						<h3 className="font-semibold text-medium text-slate-800 mb-2">
							Voice
						</h3>
						<div className="w-full">
							<Voices
								data={allVoices || []}
								onChange={(key, value) => form.setFieldValue(key, value)}
								value={form?.values?.voice_id}
								valueKey="voice_id"
								isShowCndContent={false}
							/>
						</div>
					</div>
					{_.includes(currentFields, 'emotion') &&
						<div className="col-span-full">
							<h3 className="font-semibold capitalize text-medium text-slate-800 mb-2">
								{_.find(currentFields, 'emotion')}
							</h3>
							<div className="w-full">
								<Select
									variant="bordered"
									aria-label="Select Emotion"
									aria-labelledby="Select Emotion"
									placeholder="Select Emotion"
									classNames={{
										trigger: 'shadow-none min-h-14 border border-slate-800/10 bg-white',
									}}
									disallowEmptySelection
									selectedKeys={_.isEmpty(form.values?.emotion) ? '' : [form.values?.emotion]}
									onSelectionChange={(selected) => form.setFieldValue('emotion', selected?.currentKey)}
								>
									{config?.emotions?.map((item) => (
										<SelectItem
											key={item}
											title={_.startCase(item)}
											textValue={_.startCase(item)}
										/>
									))}
								</Select>
							</div>
						</div>
					}
					{_.some(['speed', 'rate'], (field) => _.includes(currentFields, field)) &&
						<div className="col-span-full">
							<h3 className="font-semibold capitalize text-medium text-slate-800 mb-2">
								{_.find(['speed', 'rate'], (field) => _.includes(currentFields, field))}
							</h3>
							<div className="w-full">
								<div className="flex justify-between items-center">
									<span className="text-tiny text-slate-500">
										Low
									</span>
									<span className="text-tiny text-slate-500">
										High
									</span>
								</div>
								<Slider
									className="w-full"
									color="foreground"
									aria-label="speed"
									aria-labelledby="speed"
									formatOptions={{signDisplay: "always"}}
									defaultValue={1}
									value={form.values?.speed}
									onChange={(value) => form.setFieldValue('speed', value)}
									showTooltip
									maxValue={3}
									minValue={0.10}
									size="sm"
									step={0.10}
								/>
							</div>
						</div>
					}
					{_.some(['stability', 'pitch'], (field) => _.includes(currentFields, field)) &&
						<div className="col-span-full">
							<h3 className="font-semibold capitalize text-medium text-slate-800 mb-2">
								{_.find(['stability', 'pitch'], (field) => _.includes(currentFields, field))}
							</h3>
							<div className="w-full">
								<div className="flex justify-between items-center">
									<span className="text-tiny text-slate-500">
										Low
									</span>
									<span className="text-tiny text-slate-500">
										High
									</span>
								</div>
								<Slider
									className="w-full"
									color="foreground"
									aria-label="Stability"
									aria-labelledby="Stability"
									//formatOptions={{style: "percent"}}
									formatOptions={{signDisplay: "always"}}
									value={form.values?.stability}
									onChange={(value) => form.setFieldValue('stability', value)}
									showTooltip size="sm"
									defaultValue={0}
									maxValue={selectedModel.provider == 'edge_tts' ? 10 : 50}
									minValue={selectedModel.provider == 'edge_tts' ? -10 : -50}
									step={selectedModel.provider == 'edge_tts' ? 0.1 : 1}
								/>
							</div>
						</div>
					}
					{_.includes(currentFields, 'style') &&
						<div className="col-span-full">
							<h3 className="font-semibold capitalize text-medium text-slate-800 mb-2">
								{_.find(currentFields, 'style')}
							</h3>
							<div className="w-full">
								<div className="flex justify-between items-center">
									<span className="text-tiny text-slate-500">
										Low
									</span>
									<span className="text-tiny text-slate-500">
										High
									</span>
								</div>
								<Slider
									className="w-full"
									color="foreground"
									aria-label="Style Exaggeration"
									aria-labelledby="Style Exaggeration"
									formatOptions={{style: "percent"}}
									value={form.values?.style}
									onChange={(value) => form.setFieldValue('style', value)}
									showTooltip
									size="sm"
									defaultValue={0.4}
									maxValue={1}
									minValue={0.1}
									step={0.01}
								/>
							</div>
						</div>
					}
					<div>
						{_.includes(currentFields, 'use_speaker_boost') &&
							<Switch
								color="primary"
								onValueChange={(value) => form.setFieldValue('use_speaker_boost', value)}
								isSelected={form.values?.use_speaker_boost}
							>
								{_.capitalize(currentFields, 'use_speaker_boost')}
							</Switch>
						}
					</div>
					<div className="text-right">
						<Button onPress={onResetValues} color="default">Reset values</Button>
					</div>
				</div>
			</div>
		</>
	);
}
